import { supabase } from "@/lovable-demo/runtime/client";

/**
 * Data source lineage / provenance and data-quality status.
 *
 * Read helpers plus a recorder used when data is written or refreshed. No screen
 * consumes this in Package #1; the surfaces come with later packages.
 */

export interface LineageInput {
  entityType: "program" | "problem_record" | "claim" | "supplier";
  entityId: string;
  sourceSystemId?: string;
  sourceReference?: string;
  sourceTimestamp?: string;
  transformation?: string;
  normalization?: string;
  dataQualityStatus?: "unverified" | "verified" | "suspect";
  notes?: string;
}

export async function recordLineage(input: LineageInput): Promise<void> {
  try {
    const sourceTimestamp = input.sourceTimestamp ?? new Date().toISOString();
    const ingestedAt = new Date().toISOString();
    const freshnessHours =
      (new Date(ingestedAt).getTime() - new Date(sourceTimestamp).getTime()) / 3_600_000;
    await supabase.from("data_lineage").upsert(
      {
        entity_type: input.entityType,
        entity_id: input.entityId,
        source_system_id: input.sourceSystemId ?? "synthetic-seed",
        source_reference: input.sourceReference ?? "",
        source_timestamp: sourceTimestamp,
        ingested_at: ingestedAt,
        transformation: input.transformation ?? "In-application update",
        normalization: input.normalization ?? "Broadview canonical model v1",
        freshness_hours: Math.max(0, Math.round(freshnessHours * 100) / 100),
        freshness_status:
          freshnessHours <= 24 ? "current" : freshnessHours <= 168 ? "aging" : "stale",
        data_quality_status: input.dataQualityStatus ?? "unverified",
        notes: input.notes ?? "",
      },
      { onConflict: "entity_type,entity_id,source_system_id" },
    );
  } catch {
    /* non-blocking */
  }
}

export function logLineage(input: LineageInput): void {
  void recordLineage(input);
}

export async function getLineage(entityType: string, entityId: string) {
  const { data } = await supabase
    .from("data_lineage")
    .select("*, source_systems(*)")
    .eq("entity_type", entityType)
    .eq("entity_id", entityId);
  return data ?? [];
}

export async function getQualityChecks(entityType: string, entityId: string) {
  const { data } = await supabase
    .from("data_quality_checks")
    .select("*")
    .eq("entity_type", entityType)
    .eq("entity_id", entityId);
  return data ?? [];
}

export async function listSourceSystems() {
  const { data } = await supabase.from("source_systems").select("*").order("name");
  return data ?? [];
}

export async function listIntegrationRuns(limit = 25) {
  const { data } = await supabase
    .from("integration_runs")
    .select("*")
    .order("started_at", { ascending: false })
    .limit(limit);
  return data ?? [];
}
